import {
  HOME_STATIC_MOBILE_SCENE_PATH,
  HOME_STATIC_SCENE_PATH,
} from "../home/homeScene";
import type { District } from "./districts";

export const OWNER_VISUAL_PACK_ID = "owner-approved-v1";
export const LEGACY_VISUAL_PACK_ID = "legacy-phase3-v3";
export const HOME_FOUNDATION_PACK_ID = "phase4j3-home";
const PACK_ROOT = "/world/visual-packs/owner-approved-v1/assets";

type OwnerAssetId =
  | "1006686"
  | "1007102"
  | "1007179"
  | "130506"
  | "201394"
  | "2176191"
  | "2397715"
  | "2397734"
  | "2397741"
  | "692083";

interface OwnerVisualAsset {
  assetId: OwnerAssetId;
  label: string;
  districtId: string;
  routes: readonly string[];
  role:
    | "district-primary"
    | "route-alternate"
    | "home-travel"
    | "transition-overlay";
}

export interface VisualScene {
  packId: string;
  assetId: string;
  label: string;
  desktopUrl: string;
  mobileUrl?: string;
  ownerApproved: boolean;
}

const OWNER_ASSETS: Record<OwnerAssetId, OwnerVisualAsset> = {
  "1006686": {
    assetId: "1006686",
    label: "Knowledge Night Skyline",
    districtId: "knowledge-terraces",
    routes: ["/insights", "/discussion/:id"],
    role: "route-alternate",
  },
  "1007102": {
    assetId: "1007102",
    label: "Resident Cloud Street",
    districtId: "resident-quarter",
    routes: ["/profile", "/inbox"],
    role: "district-primary",
  },
  "1007179": {
    assetId: "1007179",
    label: "Service Transit Corridor",
    districtId: "service-pavilion",
    routes: ["/services", "/services/:slug", "/bookings"],
    role: "district-primary",
  },
  "130506": {
    assetId: "130506",
    label: "Market Night District",
    districtId: "market-quarter",
    routes: ["/market", "/market/:slug", "/cart"],
    role: "district-primary",
  },
  "201394": {
    assetId: "201394",
    label: "Garden Cloud Passage",
    districtId: "garden-commons",
    routes: ["/"],
    role: "home-travel",
  },
  "2176191": {
    assetId: "2176191",
    label: "Academy High-Sky Route",
    districtId: "academy-ridge",
    routes: ["/learning", "/certificate", "/certificate/:code"],
    role: "route-alternate",
  },
  "2397715": {
    assetId: "2397715",
    label: "Knowledge Blue Pavilion",
    districtId: "knowledge-terraces",
    routes: ["/library"],
    role: "district-primary",
  },
  "2397734": {
    assetId: "2397734",
    label: "Academy Mountain Quarter",
    districtId: "academy-ridge",
    routes: ["/academy", "/academy/:slug"],
    role: "district-primary",
  },
  "2397741": {
    assetId: "2397741",
    label: "Celestial Transition Veil",
    districtId: "global",
    routes: ["*"],
    role: "transition-overlay",
  },
  "692083": {
    assetId: "692083",
    label: "Private Intelligence Night",
    districtId: "resident-quarter",
    routes: ["/ai"],
    role: "route-alternate",
  },
};

const ROUTE_ASSET_ORDER: readonly OwnerAssetId[] = [
  "692083",
  "1006686",
  "2176191",
  "1007102",
  "1007179",
  "130506",
  "2397715",
  "2397734",
  "201394",
];

function matchesRoute(pathname: string, family: string) {
  if (!family.includes(":")) return pathname === family;
  const base = family.slice(0, family.lastIndexOf("/"));
  return (
    pathname.startsWith(`${base}/`) &&
    pathname.length > base.length + 1 &&
    !pathname.slice(base.length + 1).includes("/")
  );
}

function configuredVisualPack() {
  const override = new URLSearchParams(window.location.search).get(
    "visualPack",
  );
  return override === "legacy" ? "legacy" : OWNER_VISUAL_PACK_ID;
}

function ownerScene(asset: OwnerVisualAsset): VisualScene {
  return {
    packId: OWNER_VISUAL_PACK_ID,
    assetId: asset.assetId,
    label: asset.label,
    desktopUrl: `${PACK_ROOT}/${asset.assetId}.webp`,
    mobileUrl: `${PACK_ROOT}/${asset.assetId}-mobile.webp`,
    ownerApproved: true,
  };
}

function ownerAssetForPath(pathname: string) {
  return ROUTE_ASSET_ORDER.map((assetId) => OWNER_ASSETS[assetId]).find(
    (asset) => asset.routes.some((route) => matchesRoute(pathname, route)),
  );
}

function legacyScene(
  district: Pick<
    District,
    "districtId" | "districtLabel" | "sceneId" | "outputPath"
  >,
): VisualScene {
  if (district.districtId === "garden-commons") {
    return {
      packId: HOME_FOUNDATION_PACK_ID,
      assetId: "home-real3d-static",
      label: "Phase 4J3 Real-3D Homepage",
      desktopUrl: HOME_STATIC_SCENE_PATH,
      mobileUrl: HOME_STATIC_MOBILE_SCENE_PATH,
      ownerApproved: true,
    };
  }
  return {
    packId: LEGACY_VISUAL_PACK_ID,
    assetId: district.sceneId,
    label: district.districtLabel,
    desktopUrl: `/${district.outputPath}`,
    ownerApproved: true,
  };
}

export function staticVisualForPath(
  pathname: string,
  district: Pick<
    District,
    "districtId" | "districtLabel" | "sceneId" | "outputPath"
  >,
  forceLegacy = false,
): VisualScene {
  const legacy = legacyScene(district);
  if (forceLegacy || pathname === "/" || configuredVisualPack() === "legacy") {
    return legacy;
  }
  const asset = ownerAssetForPath(pathname);
  return asset ? ownerScene(asset) : legacy;
}

export function travelVisualForPath(
  pathname: string,
  district: Pick<
    District,
    "districtId" | "districtLabel" | "sceneId" | "outputPath"
  >,
): VisualScene {
  const legacy = legacyScene(district);
  if (configuredVisualPack() === "legacy") return legacy;
  const asset = ownerAssetForPath(pathname);
  return asset ? ownerScene(asset) : legacy;
}

export function transitionOverlayScene(): VisualScene | undefined {
  if (configuredVisualPack() === "legacy") return undefined;
  return ownerScene(OWNER_ASSETS["2397741"]);
}

export function activeVisualPackId() {
  return configuredVisualPack() === "legacy"
    ? LEGACY_VISUAL_PACK_ID
    : OWNER_VISUAL_PACK_ID;
}
